import { ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseButton } from "@/components/atomic/BaseButton";
import Checkbox from "@/components/atomic/Checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/atomic/DropdownMenu";
import Label from "@/components/atomic/Label";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import useConfig from "@/hooks/useConfig";
import { useDisplayGroups } from "@/hooks/useDisplayGroups";
import { cn } from "@/lib/utils";

type Option = {
  id: string;
  label: string;
};

interface ItemProps {
  option: Option;
  isVisible: boolean;
  handleToggle: (id: string) => void;
}

const Item: React.FC<ItemProps> = (
  { option, isVisible, handleToggle },
) => {
  return (
    <div
      key={option.id}
      className={cn(
        "flex items-center h-10 w-full space-x-2.5 px-5 rounded-[10px] cursor-pointer hover:bg-muted transition-colors border-[#5F6368]",
        isVisible && "bg-[#6888E1] text-white",
        "hover:bg-[#6888E1] hover:text-white hover:border-white",
      )}
      onClick={() => handleToggle(option.id)}
      onKeyDown={(e) => e.key === " " && handleToggle(option.id)}
      tabIndex={0}
      role="button"
    >
      <Checkbox
        id={`${option.id}-checkbox`}
        checked={isVisible}
        onCheckedChange={() => handleToggle(option.id)}
        className={cn(
          "inline my-2.5 border-inherit",
          isVisible && "border-white data-[state=checked]:bg-transparent",
        )}
        onClick={(e) => e.stopPropagation()}
      />
      <Label
        htmlFor={`${option.id}-checkbox`}
        className="cursor-pointer leading-10 truncate font-normal"
        onClick={(e) => e.stopPropagation()}
      >
        {option.label}
      </Label>
    </div>
  );
};

interface Props {
  onChange: (hiddenIds: string[]) => void;
  hidden: string[];
  className?: string;
}

const DisplayGroupSelect: React.FC<Props> = ({
  onChange,
  hidden = [],
  className,
}: Props) => {
  const { t } = useTranslation("voiceek_ui");
  const { config } = useConfig();
  const { displayGroups } = useDisplayGroups();
  const [open, setOpen] = useState(false);

  const overviewOption = { id: "overview", label: t("departments.all") };
  const options = displayGroups?.map((dg) => ({ id: dg.id, label: dg.name })) ?? [];

  const handleToggle = (id: string) => {
    const newHidden = hidden.includes(id)
      ? hidden.filter((item) => item !== id) // 非表示リストから削除（表示にする）
      : [...hidden, id]; // 非表示リストに追加（非表示にする）
    onChange(newHidden);
  };

  const handleDeselectAll = () => {
    // 全ての表示グループを非表示リストに追加
    const allIds = ["overview", ...(displayGroups?.map((dg) => dg.id) || [])];
    onChange(allIds);
  };

  // 表示されている（非表示リストに含まれていない）グループのラベルを計算
  const selectedLabels = [overviewOption, ...options]
    .filter((option) => !hidden.includes(option.id))
    .map((option) => option.label);

  return (
    <div className={cn("relative w-[300px]", className)}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <BaseButton
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full p-5 justify-between min-h-10 text-left border shadow-xs"
          >
            <div className="max-w-[90%] truncate">
              {selectedLabels.length > 0
                ? selectedLabels.join(", ")
                : (
                  <span className="text-muted-foreground">
                    {t("departments.placeholder", { displayGroupLabel: config?.displayGroupLabel })}
                  </span>
                )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </BaseButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="px-0 py-0 gap-2.5 w-[300px] h-full" align="start" side="bottom" sideOffset={4}>
          <ScrollArea className="px-4 box-content">
            <div className="max-h-[480px] pt-4">
              <div>
                <BaseButton
                  variant="ghost"
                  className={cn(
                    "h-10 px-5 mb-2.5 hover:text-foreground w-full justify-start rounded-[10px] font-normal",
                    "hover:bg-[#6888E1] hover:text-white",
                  )}
                  onClick={handleDeselectAll}
                >
                  {t("departments.deselectAll")}
                </BaseButton>
              </div>

              <div className="flex flex-col pb-4 truncate gap-2.5 w-[265px]">
                <Item option={overviewOption} isVisible={!hidden.includes("overview")} handleToggle={handleToggle} />
                <Label className="my-[5px] text-[#5F6368] font-bold">
                  {t("departments.select", { displayGroupLabel: config?.displayGroupLabel })}
                </Label>
                {options.map((option) => (
                  <Item
                    key={option.id}
                    option={option}
                    isVisible={!hidden.includes(option.id)}
                    handleToggle={handleToggle}
                  />
                ))}
              </div>
              <ScrollBar orientation="vertical" />
            </div>
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default DisplayGroupSelect;
